/**
 * Chat Service - Supabase-based chat operations with real-time subscriptions
 */

import { supabase } from './supabase';
import { getProfilePictureUrl } from './storage';

// =====================
// CONVERSATIONS
// =====================

/**
 * Get all conversations for a user
 */
export const getUserConversations = async (userId) => {
  // Get conversation IDs for user
  const { data: conversationUsers, error: cuError } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', userId);
  
  if (cuError) throw cuError;
  
  if (!conversationUsers || conversationUsers.length === 0) {
    return [];
  }
  
  const conversationIds = conversationUsers.map(cu => cu.conversation_id);
  
  // Get conversations with participants and latest message
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      id,
      last_message_at,
      created_at,
      conversation_users (
        user_id,
        users (
          id,
          username,
          profile_picture_url,
          account_type,
          club_name,
          is_online
        )
      )
    `)
    .in('id', conversationIds)
    .order('last_message_at', { ascending: false, nullsFirst: false });
  
  if (error) throw error;
  
  // Get the latest message for each conversation
  const conversationsWithMessages = await Promise.all(
    data.map(async (conv) => {
      const { data: messages } = await supabase
        .from('messages')
        .select('id, content, created_at, sender_id, read')
        .eq('conversation_id', conv.id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      return {
        ...conv,
        latestMessage: messages?.[0] || null,
      };
    })
  );

  // Only return conversations that have at least one message (no empty chats in list)
  return conversationsWithMessages.filter((conv) => conv.latestMessage != null);
};

/**
 * Get or create a conversation between two users
 * Only creates conversation if one doesn't exist
 */
export const getOrCreateConversation = async (user1Id, user2Id) => {
  // Find existing conversation
  const { data: user1Convs } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user1Id);
  
  const { data: user2Convs } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user2Id);
  
  // Find intersection
  const user1ConvIds = new Set(user1Convs?.map(c => c.conversation_id) || []);
  const commonConv = user2Convs?.find(c => user1ConvIds.has(c.conversation_id));
  
  if (commonConv) {
    // Return existing conversation
    const { data } = await supabase
      .from('conversations')
      .select(`
        id,
        last_message_at,
        conversation_users (
          user_id,
          users (*)
        )
      `)
      .eq('id', commonConv.conversation_id)
      .single();
    
    if (data) {
      console.log('✅ Found existing conversation:', data.id);
      return data;
    }
  }

  // Re-check for existing conversation right before insert (handles race when both users send at once)
  const { data: user1Convs2 } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user1Id);
  const { data: user2Convs2 } = await supabase
    .from('conversation_users')
    .select('conversation_id')
    .eq('user_id', user2Id);
  const user1Set = new Set(user1Convs2?.map((c) => c.conversation_id) || []);
  const commonAgain = user2Convs2?.find((c) => user1Set.has(c.conversation_id));
  if (commonAgain) {
    const { data: existing } = await supabase
      .from('conversations')
      .select('id, last_message_at, conversation_users ( user_id, users (*) )')
      .eq('id', commonAgain.conversation_id)
      .single();
    if (existing) {
      console.log('✅ Found existing conversation (re-check):', existing.id);
      return existing;
    }
  }
  
  // Only create new conversation if none exists (creates when first message is sent)
  console.log('➕ Creating new conversation between users');
  const { data: newConv, error: convError } = await supabase
    .from('conversations')
    .insert([{ last_message_at: null }])
    .select()
    .single();
  
  if (convError) throw convError;
  
  // Add both users
  await supabase
    .from('conversation_users')
    .insert([
      { conversation_id: newConv.id, user_id: user1Id },
      { conversation_id: newConv.id, user_id: user2Id },
    ]);
  
  console.log('✅ Created new conversation:', newConv.id);
  return newConv;
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (conversationId) => {
  const { error } = await supabase
    .from('conversations')
    .delete()
    .eq('id', conversationId);
  
  if (error) throw error;
};

// =====================
// MESSAGES
// =====================

/**
 * Get messages for a conversation
 */
export const getMessages = async (conversationId, limit = 50) => {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      id,
      content,
      created_at,
      read,
      sender_id,
      receiver_id,
      sender:users!messages_sender_id_fkey (
        id, username, profile_picture_url, account_type
      ),
      receiver:users!messages_receiver_id_fkey (
        id, username, profile_picture_url, account_type
      )
    `)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  
  if (error) throw error;
  return data?.reverse() || [];
};

/**
 * Send a message
 */
export const sendMessage = async (conversationId, senderId, receiverId, content) => {
  console.log('📤 chatService.sendMessage called with:', {
    conversationId,
    senderId,
    receiverId,
    contentLength: content?.length,
  });

  // Validate inputs
  if (!conversationId) {
    throw new Error('conversationId is required');
  }
  if (!senderId) {
    throw new Error('senderId is required');
  }
  if (!receiverId) {
    throw new Error('receiverId is required');
  }
  if (!content || !content.trim()) {
    throw new Error('content is required');
  }

  // If conversationId is a draft (starts with "draft-"), create the conversation first
  let actualConversationId = conversationId;
  if (conversationId.startsWith('draft-')) {
    console.log('📝 Draft conversation detected, creating conversation before sending message');
    
    // Check if conversation already exists between these users
    const existingConv = await getOrCreateConversation(senderId, receiverId);
    actualConversationId = existingConv.id;
    console.log('✅ Using conversation:', actualConversationId);
  }

  // Insert message
  const { data: message, error: msgError } = await supabase
    .from('messages')
    .insert([{
      conversation_id: actualConversationId,
      sender_id: senderId,
      receiver_id: receiverId,
      content: content.trim(),
      read: false,
      // Don't set created_at manually - let database default handle it
    }])
    .select(`
      id,
      content,
      created_at,
      read,
      sender_id,
      receiver_id,
      conversation_id,
      sender:users!messages_sender_id_fkey (
        id, username, profile_picture_url, account_type
      ),
      receiver:users!messages_receiver_id_fkey (
        id, username, profile_picture_url, account_type
      )
    `)
    .single();
  
  if (msgError) {
    console.error('❌ Error inserting message:', msgError);
    console.error('Error details:', {
      code: msgError.code,
      message: msgError.message,
      details: msgError.details,
      hint: msgError.hint,
    });
    throw msgError;
  }

  if (!message) {
    throw new Error('Message was not created - no data returned');
  }

  console.log('✅ Message inserted successfully:', {
    id: message.id,
    conversationId: message.conversation_id,
    senderId: message.sender_id,
    receiverId: message.receiver_id,
  });
  
  // Update conversation's last_message_at
  const { error: updateError } = await supabase
    .from('conversations')
    .update({ last_message_at: new Date().toISOString() })
    .eq('id', actualConversationId);
  
  if (updateError) {
    console.error('⚠️ Error updating conversation last_message_at:', updateError);
    // Don't throw - message was created successfully
  }
  
  return message;
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId, userId) => {
  const { error } = await supabase
    .from('messages')
    .update({ read: true })
    .eq('conversation_id', conversationId)
    .eq('receiver_id', userId)
    .eq('read', false);
  
  if (error) throw error;
};

/**
 * Get unread message count for a user
 */
export const getUnreadCount = async (userId) => {
  const { count, error } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('receiver_id', userId)
    .eq('read', false);
  
  if (error) throw error;
  return count || 0;
};

/**
 * Get unread message count from a specific user
 * @param {string} currentUserId - The current user's ID
 * @param {string} otherUserId - The other user's ID
 * @returns {Promise<number>} Number of unread messages from that user
 */
export const getUnreadCountFromUser = async (currentUserId, otherUserId) => {
  try {
    // Find conversation between users
    const conversation = await getOrCreateConversation(currentUserId, otherUserId);
    
    if (!conversation || !conversation.id) {
      return 0;
    }
    
    // Count unread messages where current user is receiver
    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', conversation.id)
      .eq('receiver_id', currentUserId)
      .eq('read', false);
    
    if (error) {
      console.error('Error getting unread count from user:', error);
      return 0;
    }
    
    return count || 0;
  } catch (error) {
    console.error('Error in getUnreadCountFromUser:', error);
    return 0;
  }
};

// =====================
// REAL-TIME SUBSCRIPTIONS
// =====================

/**
 * Subscribe to new messages for a user
 * @param {string} userId - The user ID to watch for messages
 * @param {function} onMessage - Callback when new message arrives
 * @returns {function} Unsubscribe function
 */
export const subscribeToMessages = (userId, onMessage) => {
  const channel = supabase
    .channel(`messages-${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `receiver_id=eq.${userId}`,
      },
      (payload) => {
        console.log('🔔 New message received:', payload.new.id);
        onMessage(payload.new);
      }
    )
    .subscribe();
  
  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Subscribe to messages in a specific conversation
 * @param {string} conversationId - The conversation to watch
 * @param {function} onMessage - Callback when new message arrives
 * @returns {function} Unsubscribe function
 */
export const subscribeToConversation = (conversationId, onMessage) => {
  const channel = supabase
    .channel(`conversation-${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => {
        console.log('🔔 New message in conversation:', payload.new.id);
        onMessage(payload.new);
      }
    )
    .subscribe();
  
  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Subscribe to message read status updates
 */
export const subscribeToReadStatus = (conversationId, onUpdate) => {
  const channel = supabase
    .channel(`read-status-${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload) => {
        if (payload.new.read !== payload.old.read) {
          onUpdate(payload.new);
        }
      }
    )
    .subscribe();
  
  return () => {
    supabase.removeChannel(channel);
  };
};

// =====================
// HELPER FUNCTIONS
// =====================

/**
 * Format conversation for UI (snake_case to camelCase)
 */
export const formatConversation = (conv, currentUserId) => {
  // Get the other user in the conversation
  const otherUser = conv.conversation_users?.find(
    cu => cu.user_id !== currentUserId
  )?.users;
  
  return {
    id: conv.id,
    lastMessageAt: conv.last_message_at,
    createdAt: conv.created_at,
    otherUser: otherUser ? {
      id: otherUser.id,
      username: otherUser.username,
      profilePictureUrl: otherUser.profile_picture_url,
      accountType: otherUser.account_type,
      clubName: otherUser.club_name,
      isOnline: otherUser.is_online,
    } : null,
    latestMessage: conv.latestMessage ? {
      id: conv.latestMessage.id,
      content: conv.latestMessage.content,
      createdAt: conv.latestMessage.created_at,
      senderId: conv.latestMessage.sender_id,
      read: conv.latestMessage.read,
    } : null,
  };
};

/**
 * Format message for UI
 */
export const formatMessage = (msg) => ({
  id: msg.id,
  content: msg.content,
  createdAt: msg.created_at,
  read: msg.read,
  senderId: msg.sender_id,
  receiverId: msg.receiver_id,
  sender: msg.sender ? {
    id: msg.sender.id,
    username: msg.sender.username,
    profilePictureUrl: msg.sender.profile_picture_url,
    accountType: msg.sender.account_type,
  } : null,
  receiver: msg.receiver ? {
    id: msg.receiver.id,
    username: msg.receiver.username,
    profilePictureUrl: msg.receiver.profile_picture_url,
    accountType: msg.receiver.account_type,
  } : null,
});

// =====================
// ADMIN FUNCTIONS
// =====================

/**
 * Clear ALL chats from the database (admin only)
 * Deletes all conversations - CASCADE will remove messages and conversation_users
 * @returns {Promise<{deletedConversations: number, deletedMessages: number}>}
 */
export const clearAllChats = async () => {
  // First, get counts for reporting
  const { count: messageCount } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true });

  const { count: conversationCount } = await supabase
    .from('conversations')
    .select('*', { count: 'exact', head: true });

  console.log(`🗑️ Clearing all chats: ${conversationCount} conversations, ${messageCount} messages`);

  // Delete all messages first (for safety, even though CASCADE should handle it)
  const { error: msgError } = await supabase
    .from('messages')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (neq a non-existent ID)

  if (msgError) {
    console.error('Error deleting messages:', msgError);
    throw msgError;
  }

  // Delete all conversation_users
  const { error: cuError } = await supabase
    .from('conversation_users')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000');

  if (cuError) {
    console.error('Error deleting conversation_users:', cuError);
    throw cuError;
  }

  // Delete all conversations
  const { error: convError } = await supabase
    .from('conversations')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000');

  if (convError) {
    console.error('Error deleting conversations:', convError);
    throw convError;
  }

  console.log('✅ All chats cleared successfully');
  return {
    deletedConversations: conversationCount || 0,
    deletedMessages: messageCount || 0,
  };
};

export default {
  getUserConversations,
  getOrCreateConversation,
  deleteConversation,
  getMessages,
  sendMessage,
  markMessagesAsRead,
  getUnreadCount,
  getUnreadCountFromUser,
  subscribeToMessages,
  subscribeToConversation,
  subscribeToReadStatus,
  formatConversation,
  formatMessage,
  clearAllChats,
};

